import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Card, Button, Image, Text } from '@chakra-ui/react';
import { downloadFile } from '../../helpers';
import { TState } from '../../redux/store';
import { NormalIcon, allIcons, icons } from '../utils/Icon';

export const getComponentByFileType = (file: File, fileUrl?: string) => {
  const fUrl = fileUrl || URL.createObjectURL(file);
  let component;

  switch (true) {
    case /pdf/i.test(file?.type):
      component = (
        <iframe className='pdfViewer' src={fUrl} title='PDF Viewer' />
      );
      break;
    case /image/i.test(file?.type):
      component = (
        <Image className='imgViewer' src={fUrl} alt='Image preview' />
      );
      break;
    case /audio/i.test(file?.type):
      component = <audio controls src={fUrl} />;
      break;
    case /video/i.test(file?.type):
      component = (
        <video controls className='imgViewer' key={fUrl}>
          <source src={fUrl} type='video/mp4' />
          Votre navigateur ne supporte pas le tag vidéo.
        </video>
      );
      break;
    case !file?.type:
      component = <p>Pas de fichier</p>;
      break;
    default:
      component = <p>Type de fichier non supporté.</p>;
  }

  return component;
};

export const FileViewer = ({
  curr = 0,
  fileUrl,
  files,
  canDelete,
  canDownload,
  handleDelete,
  setCurr,
}: {
  curr?: number;
  fileUrl: string;
  files: File[];
  canDelete?: boolean;
  canDownload?: boolean;
  setCurr?: (nb: number) => void;
  handleDelete?: () => void;
}) => {
  const theme = useSelector((state: TState) => state.theme);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);

  const file = files[curr];
  const component = getComponentByFileType(file, fileUrl);

  const handleDownload = useCallback(
    async (fileUrl: string, filename: string) => {
      await downloadFile(fileUrl, filename, true);
    },
    [],
  );

  const handleDel = () => {
    handleDelete?.();
    setIsDeleteConfirm(false);
  };

  const handleChange = (idx: number) => {
    setIsDeleteConfirm(false);
    setCurr?.(idx);
  };

  return (
    <Flex
      userSelect='none'
      gap={3}
      h='calc(100vh - 62px - 24px)'
      direction={['column', 'column', 'column', 'row']}
    >
      <Card
        w={['100%', '100%', '100%', '30%']}
        bgColor='#f9fafb'
        p={2}
        gap={2}
        overflow='auto'
        flexShrink={0}
        h={['30%', '30%', '30%', 'auto']}
      >
        {files?.map((f: any, idx: number) => (
          <Card
            p={2}
            cursor='pointer'
            color={idx === curr ? 'white' : 'auto'}
            bgColor={idx === curr ? theme.dark : 'white'}
            onClick={() => handleChange(idx)}
            direction='row'
            alignItems='center'
            minH='56px'
          >
            <Text flexGrow={1}>{f.filename}</Text>
            {!isDeleteConfirm && canDownload && (
              <NormalIcon
                boxSize='25px'
                color={idx === curr ? 'white' : undefined}
                as={allIcons.HiMiniArrowDownOnSquareStack}
                onClick={() => handleDownload(fileUrl, f.filename)}
              />
            )}
            {!isDeleteConfirm && canDelete && (
              <NormalIcon
                boxSize='25px'
                backgroundColor='red.500'
                color='white'
                cursor='pointer'
                _hover={{
                  backgroundColor: 'white',
                  color: 'red.500',
                }}
                icon={icons.trash}
                onClick={() =>
                  setTimeout(() => {
                    setIsDeleteConfirm(true);
                  }, 1)
                }
              />
            )}
            {idx === curr && isDeleteConfirm && handleDel && (
              <>
                <Button
                  color='white'
                  bgColor={`red.500`}
                  onClick={handleDel}
                  mr={3}
                  _hover={{}}
                >
                  Confirmer
                </Button>
                <Button onClick={() => setIsDeleteConfirm(false)}>
                  Annuler
                </Button>
              </>
            )}
          </Card>
        ))}
      </Card>
      <Flex justifyContent='center' overflow='hidden' flexGrow={1}>
        {component}
      </Flex>
    </Flex>
  );
};
